import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';

import { Breadcrumb } from '../components/Breadcrumb';
import { DataTable, ColumnDef } from '../components/DataTable';
import { KpiTile } from '../components/quoteToCash/SectionCard';
import { percent } from '../components/quoteToCash/format';
import { ApprovalPanel } from '../components/salesAgent/ApprovalPanel';
import { ReviewControl } from '../components/salesAgent/ReviewControl';
import { dateTime } from '../components/salesAgent/format';
import { apiService } from '../services/apiService';
import {
  SALES_AGENT_CATEGORIES,
  SalesAgentEmail,
  SalesAgentEmails,
} from '../services/salesAgentTypes';

const CATEGORY_PILLS: Record<string, string> = {
  RFQ: 'bg-blue-100 text-blue-800',
  ORDER: 'bg-green-100 text-green-800',
  MISC: 'bg-gray-100 text-gray-700',
};

const STATUS_BADGES: Record<string, string> = {
  done: 'bg-green-100 text-green-800',
  claimed: 'bg-yellow-100 text-yellow-800',
  failed: 'bg-red-100 text-red-800',
};

const pill = 'inline-flex px-2 py-0.5 rounded-full text-xs font-medium';

/** The current month, YYYY-MM, in UTC as the API counts it */
const currentMonth = () => new Date().toISOString().slice(0, 7);

/** Text cut to one line, whole on hover */
const Truncated: React.FC<{ text?: string; width: string }> = ({
  text,
  width,
}) =>
  text ? (
    <span className={`block truncate ${width}`} title={text}>
      {text}
    </span>
  ) : (
    <>—</>
  );

/**
 * Every email the sales inbox agent handled in a month: its triage, any RFQ proposal or order check, the sales team's
 * review of the category (to measure the agent before triage goes live) and the approval of RFQ proposals
 */
export const SalesAgentPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const month = searchParams.get('month') || currentMonth();

  const [data, setData] = useState<SalesAgentEmails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Only the latest request's answer is shown, when the month changes before the previous one answered
  const requestRef = useRef(0);

  const load = useCallback(async () => {
    const request = ++requestRef.current;
    setLoading(true);
    setError(null);
    try {
      const emails = await apiService.getSalesAgentEmails(month);
      if (request === requestRef.current) setData(emails);
    } catch (err) {
      if (request === requestRef.current) {
        setError(err instanceof Error ? err.message : t('salesAgent.error'));
      }
    } finally {
      if (request === requestRef.current) setLoading(false);
    }
  }, [month, t]);

  useEffect(() => {
    load();
  }, [load]);

  const summary = data?.summary;

  const columns: ColumnDef<SalesAgentEmail>[] = [
    {
      key: 'received',
      header: t('salesAgent.columns.received'),
      className: 'whitespace-nowrap',
      render: (value) => dateTime(value as string | undefined, i18n.language),
    },
    {
      key: 'sender',
      header: t('salesAgent.columns.sender'),
      render: (value) => (
        <Truncated text={value as string | undefined} width="max-w-[12rem]" />
      ),
    },
    {
      key: 'subject',
      header: t('salesAgent.columns.subject'),
      render: (value) => (
        <Truncated text={value as string | undefined} width="max-w-[16rem]" />
      ),
    },
    {
      key: 'category',
      header: t('salesAgent.columns.category'),
      render: (_value, row) =>
        row?.category ? (
          <span className="flex flex-col items-start gap-0.5">
            <span className={`${pill} ${CATEGORY_PILLS[row.category] ?? ''}`}>
              {t(`salesAgent.category.${row.category}`)}
            </span>
            {row.confidence && (
              <span className="text-xs text-gray-500">
                {t(`salesAgent.confidence.${row.confidence}`)}
              </span>
            )}
          </span>
        ) : (
          '—'
        ),
    },
    {
      key: 'reason',
      header: t('salesAgent.columns.reason'),
      render: (value) => (
        <Truncated text={value as string | undefined} width="max-w-[18rem]" />
      ),
    },
    {
      key: 'mode',
      header: t('salesAgent.columns.modeFolder'),
      render: (_value, row) => (
        <span className="flex flex-col gap-0.5">
          <span>{row?.mode ? t(`salesAgent.mode.${row.mode}`) : '—'}</span>
          {row?.folder && (
            <span className="text-xs text-gray-500">{row.folder}</span>
          )}
        </span>
      ),
    },
    {
      key: 'status',
      header: t('salesAgent.columns.status'),
      render: (_value, row) =>
        row?.status ? (
          <span
            className={`${pill} ${STATUS_BADGES[row.status] ?? ''}`}
            title={row.error}
          >
            {t(`salesAgent.status.${row.status}`)}
          </span>
        ) : (
          '—'
        ),
    },
    {
      key: 'details',
      header: t('salesAgent.columns.details'),
      render: (_value, row) => (row ? <Details email={row} /> : null),
    },
    {
      key: 'review',
      header: t('salesAgent.columns.review'),
      render: (_value, row) =>
        row ? <ReviewControl email={row} onSaved={load} /> : null,
    },
  ];

  const renderExpandedRow = (email: SalesAgentEmail) => {
    const failed = email.status === 'failed' && email.error;
    if (!email.approval_status && !failed) return null;
    return (
      <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 space-y-2">
        {failed && (
          <p className="text-sm text-red-700">
            {t('salesAgent.failedWith', { error: email.error })}
          </p>
        )}
        {email.approval_status && (
          <ApprovalPanel email={email} onDecided={load} />
        )}
      </div>
    );
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 w-full">
      <Breadcrumb currentPage={t('navigation.salesAgent')} />

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mt-2 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {t('salesAgent.title')}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {t('salesAgent.subtitle')}
          </p>
        </div>
        <div className="flex items-end gap-3">
          <label className="text-sm text-gray-700">
            <span className="block text-xs font-medium mb-1">
              {t('salesAgent.month')}
            </span>
            <input
              type="month"
              value={month}
              max={currentMonth()}
              onChange={(e) =>
                e.target.value &&
                setSearchParams({ month: e.target.value }, { replace: true })
              }
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </label>
          <button
            onClick={load}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            {t('salesAgent.refresh')}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
          <p className="font-semibold">{t('common.error')}</p>
          <p className="text-sm">{error}</p>
        </div>
      )}

      {summary && (
        <div
          className={`grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-6 ${
            loading ? 'opacity-50 transition-opacity' : ''
          }`}
        >
          <KpiTile
            label={t('salesAgent.summary.emails')}
            value={String(summary.emails)}
          />
          {SALES_AGENT_CATEGORIES.map((category) => (
            <KpiTile
              key={category}
              label={t(`salesAgent.category.${category}`)}
              value={String(summary.categories[category] ?? 0)}
            />
          ))}
          <KpiTile
            label={t('salesAgent.summary.reviewed')}
            value={String(summary.reviewed)}
          />
          <KpiTile
            label={t('salesAgent.summary.agreement')}
            value={
              summary.reviewed
                ? percent(summary.agreed / summary.reviewed, i18n.language, 0)
                : '—'
            }
            detail={
              summary.reviewed
                ? t('salesAgent.summary.agreementDetail', {
                    agreed: summary.agreed,
                    reviewed: summary.reviewed,
                  })
                : t('salesAgent.summary.noReviews')
            }
          />
        </div>
      )}

      {loading && !data ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
          <span className="ml-2 text-gray-600">{t('common.loading')}</span>
        </div>
      ) : (
        data && (
          <div className={loading ? 'opacity-50 transition-opacity' : ''}>
            <DataTable
              columns={columns}
              data={data.emails}
              keyExtractor={(row) => row.emailKey}
              renderExpandedRow={renderExpandedRow}
              emptyMessage={t('salesAgent.noEmails')}
            />
          </div>
        )
      )}
    </div>
  );
};

/** What the agent did beyond triage: the RFQ proposal (lines matched, price list, customer) or the order check */
const Details: React.FC<{ email: SalesAgentEmail }> = ({ email }) => {
  const { t } = useTranslation();
  const lines: React.ReactNode[] = [];

  if (email.rfq_lines !== undefined) {
    lines.push(
      t('salesAgent.rfq.lines', {
        matched: email.rfq_matched ?? 0,
        lines: email.rfq_lines,
      })
    );
  }
  if (email.rfq_price_list) {
    lines.push(`${t('salesAgent.rfq.priceList')}: ${email.rfq_price_list}`);
  }
  if (email.rfq_customer) {
    lines.push(`${t('salesAgent.rfq.customer')}: ${email.rfq_customer}`);
  }
  if (email.order_po) {
    lines.push(`${t('salesAgent.order.po')}: ${email.order_po}`);
  }
  if (email.order_quote_num !== undefined) {
    lines.push(`${t('salesAgent.order.quote')}: ${email.order_quote_num}`);
  }
  if (email.order_differences !== undefined) {
    lines.push(
      t('salesAgent.order.differences', { count: email.order_differences })
    );
  }
  const errors = [email.rfq_error, email.order_error, email.reply_error].filter(
    Boolean
  );

  if (lines.length === 0 && errors.length === 0) return <>—</>;
  return (
    <div className="space-y-0.5 text-xs text-gray-700 min-w-[10rem]">
      {lines.map((line, index) => (
        <p key={index}>{line}</p>
      ))}
      {errors.map((message, index) => (
        <p key={`error-${index}`} className="text-red-700">
          {message}
        </p>
      ))}
    </div>
  );
};
